import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Onboarding.css';

// Storage key prefix for page-specific onboarding
const PAGE_ONBOARDING_PREFIX = 'allocq_page_onboarding_';

/**
 * PageOnboarding - Shows a single instructional tooltip for a specific page
 * @param {string} pageId - Unique identifier for the page (e.g., 'optimize', 'results', 'backtest')
 * @param {string} userEmail - User's email for per-user tracking
 * @param {string} title - Title of the onboarding tooltip
 * @param {string} message - Message to display
 * @param {string} target - CSS selector for element to highlight (optional)
 * @param {Array} steps - Array of step objects for multi-step page onboarding (optional)
 */
const PageOnboarding = ({ pageId, userEmail, title, message, target, steps }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Use steps array if provided, otherwise create single step from props
  const onboardingSteps = steps || [{ title, message, target }];
  const step = onboardingSteps[currentStep];
  const isMultiStep = onboardingSteps.length > 1;
  const isLastStep = currentStep === onboardingSteps.length - 1;
  const isFirstStep = currentStep === 0;

  useEffect(() => {
    if (!userEmail || !pageId) return;

    const storageKey = PAGE_ONBOARDING_PREFIX + pageId + '_' + userEmail;
    const hasSeen = localStorage.getItem(storageKey);

    if (!hasSeen) {
      // Small delay to let the page render first
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [userEmail, pageId]);

  // Highlight target element
  useEffect(() => {
    if (!isVisible || !step?.target) return;

    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      targetElement.classList.add('onboarding-highlight');
      return () => {
        targetElement.classList.remove('onboarding-highlight');
      };
    }
  }, [isVisible, step]);

  const handleComplete = () => {
    setIsVisible(false);
    if (userEmail && pageId) {
      const storageKey = PAGE_ONBOARDING_PREFIX + pageId + '_' + userEmail;
      localStorage.setItem(storageKey, 'true');
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <>
      <div className="onboarding-overlay" />
      <div
        className="onboarding-tooltip"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      >
        <div className="onboarding-header">
          {isMultiStep && (
            <span className="onboarding-step-indicator">
              {currentStep + 1} / {onboardingSteps.length}
            </span>
          )}
          <button
            className="onboarding-skip"
            onClick={handleComplete}
            style={{ marginLeft: isMultiStep ? '0' : 'auto' }}
          >
            {isMultiStep ? 'Skip' : 'Got it'}
          </button>
        </div>

        <div className="onboarding-content">
          <h3 className="onboarding-title">{step.title}</h3>
          <p className="onboarding-message">{step.message}</p>
        </div>

        <div className="onboarding-footer">
          {isMultiStep && !isFirstStep && (
            <button className="onboarding-btn onboarding-btn-back" onClick={handlePrev}>
              Back
            </button>
          )}
          <button className="onboarding-btn onboarding-btn-next" onClick={handleNext}>
            {isLastStep ? 'Got it!' : 'Next'}
          </button>
        </div>

        {isMultiStep && (
          <div className="onboarding-dots">
            {onboardingSteps.map((_, index) => (
              <span
                key={index}
                className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default PageOnboarding;
